/* GASSISA
 =========================================================================================
 * Descripcion: Envio de tickets a la impresora termica local (PrinterTools).
 *
 *              Concentra lo que estaba repetido en el front (FormData + logo +
 *              printerName + POST + manejo de error) y la vista previa ESC/POS
 *              que usa Administracion > Reimpresiones.
 *
 *              El binario lo arma cada modulo con su propio contenido:
 *                - ticketVentaEscape.js  (ticket de ventas, con layout por objetos)
 *                - cajaTicketUtils.js    (ticket de cierre de caja)
 *                - los builders locales de cada pantalla
 *
 * Historial de cambios:
 * ---------------------------------------------------------------------------------------
 *    Revision   | Fecha      | Desarrollador                    | Resumen del cambio
 * ---------------------------------------------------------------------------------------
 *      1       | 20/10/2026 | Lic. Jose Luis Aragon Cervantes  | Creacion (extraccion de la logica repetida)
 * ---------------------------------------------------------------------------------------*/

import { PRINTERTOOLS_TICKET_URL, escalarLogoImpresion } from "./ticketVentaEscape";
import { concatenarPartes } from "./ticketEscPos";
import { leerMensajeErrorPrinterTools } from "./printerTools";
import { DOTS_POR_MM, MAX_LOGO_WIDTH_DOTS, MAX_COLUMNAS_LINEA } from "./ticketPerfil";

export { PRINTERTOOLS_TICKET_URL };

// Arma el FormData que espera PrinterTools: el binario del ticket, la impresora
// destino y el logo (ya escalado a dots por el diseno).
export const construirFormDataTicket = async ({
    partes,
    parametrosCajaForm = {},
    logoBase64 = null,
    logoBlob = null,
    nombreImpresora = null,
    anchoLogoMm = null,
    altoLogoMm = null,
}) => {
    const formData = new FormData();
    formData.append("ticket", new Blob([concatenarPartes(partes)], { type: "application/octet-stream" }));
    formData.append(
        "printerName",
        (nombreImpresora ?? parametrosCajaForm?.nombreimpresoraventas ?? "").trim()
    );

    // Logo ya en Blob (descargado de una URL)
    if (logoBlob) {
        formData.append("logo", logoBlob, "logocliente.png");
        return formData;
    }

    const logo = logoBase64 ?? parametrosCajaForm?.logo ?? null;
    if (logo) {
        let logoParaImpresion = logo;
        const ancho = anchoLogoMm ?? parametrosCajaForm?.ticketLayout?.logo?.ancho ?? null;
        const alto = altoLogoMm ?? parametrosCajaForm?.ticketLayout?.logo?.alto ?? null;
        if (ancho && Number(ancho) > 0) {
            try {
                logoParaImpresion = (await escalarLogoImpresion(logo, ancho, alto)) || logo;
            } catch {
                logoParaImpresion = logo;
            }
        }
        const binary = atob(logoParaImpresion);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        formData.append("logo", new Blob([bytes], { type: "image/png" }), "logocliente.png");
    }

    return formData;
};

// Envia el ticket a la impresora. Lanza Error con el mensaje real de PrinterTools.
export const imprimirTicketEnPrinterTools = async (opciones = {}) => {
    const formData = await construirFormDataTicket(opciones);
    const response = await fetch(PRINTERTOOLS_TICKET_URL, {
        method: "POST",
        body: formData,
    });
    if (!response.ok) {
        throw new Error(await leerMensajeErrorPrinterTools(response, response.statusText));
    }
    return true;
};

// Convierte el binario ESC/POS en lineas legibles para la vista previa,
// respetando alineacion, negrita y doble tamano.
//
// PrinterTools acepta hasta 96 caracteres por linea (line[:96]) y la impresora
// ajusta al ancho fisico. La vista previa respeta columnasMax para mostrar el
// texto completo en varias lineas, sin perder caracteres.
export const parseEscPosPreview = (bytes, opciones = {}) => {
    const columnasMax = typeof opciones === "number"
        ? opciones
        : (opciones.columnasMax ?? MAX_COLUMNAS_LINEA);
    const anchoCaracterDots = typeof opciones === "object"
        ? (opciones.anchoCaracterDots ?? 9)
        : 9;

    const decoder = new TextDecoder("utf-8");
    const lineasFisicas = [];
    let inicioLinea = 0;

    for (let i = 0; i <= bytes.length; i++) {
        if (i === bytes.length || bytes[i] === 0x0A) {
            lineasFisicas.push(decoder.decode(bytes.slice(inicioLinea, i)));
            inicioLinea = i + 1;
        }
    }

    const lines = [];
    let state = { align: "left", bold: false, large: false };

    lineasFisicas.forEach((lineaFisica) => {
        let line = "";
        let i = 0;
        let xDots = 0;
        let pendienteX = false;
        let lineTieneX = false;
        let lineAlign = state.align;
        let lineBold = state.bold;
        let lineLarge = state.large;

        const pushLine = () => {
            const texto = line;
            const partesLinea = columnasMax > 0 && texto.length > columnasMax
                ? Array.from({ length: Math.ceil(texto.length / columnasMax) }, (_, indice) => (
                    texto.slice(indice * columnasMax, (indice + 1) * columnasMax)
                ))
                : [texto];

            partesLinea.forEach((parte, indice) => {
                lines.push({
                    text: parte,
                    align: indice === 0 && !lineTieneX ? lineAlign : "left",
                    bold: lineBold,
                    large: lineLarge,
                });
            });

            line = "";
            xDots = 0;
            pendienteX = false;
            lineTieneX = false;
            lineAlign = state.align;
            lineBold = state.bold;
            lineLarge = state.large;
        };

        const aplicarX = () => {
            if (!pendienteX) return;
            const columna = Math.max(0, Math.round(xDots / anchoCaracterDots));
            if (columna > line.length) line += " ".repeat(columna - line.length);
            if (xDots > 0) lineTieneX = true;
            pendienteX = false;
        };

        while (i < lineaFisica.length) {
            const c = lineaFisica[i];

            if (c === "\x1B") {
                const comando = lineaFisica[i + 1];
                i += 2;
                switch (comando) {
                    case "a": { // ESC a n  (alineacion)
                        const align = lineaFisica.charCodeAt(i) || 0;
                        i += 1;
                        state = { ...state, align: align === 1 ? "center" : align === 2 ? "right" : "left" };
                        break;
                    }
                    case "!": { // ESC ! n  (fuente / tamano / negrita)
                        const code = lineaFisica.charCodeAt(i) || 0;
                        i += 1;
                        state = {
                            ...state,
                            large: (code & 0x30) !== 0,
                        };
                        break;
                    }
                    case "E": { // ESC E n  (negrita)
                        const bold = lineaFisica.charCodeAt(i) || 0;
                        i += 1;
                        state = { ...state, bold: bold === 1 };
                        break;
                    }
                    case "M": { // ESC M n  (fuente A/B)
                        i += 1;
                        break;
                    }
                    case "d": { // ESC d n  (avanza n lineas)
                        const count = Number(lineaFisica[i] || "0");
                        i += 1;
                        for (let j = 0; j < count; j++) pushLine();
                        break;
                    }
                    case "K": // ESC K n
                    case "3": // ESC 3 n
                    case "t": // ESC t n
                    case "2": // ESC 2
                        if (comando !== "2") i += 1;
                        break;
                    case "$": { // ESC $ nL nH  (posicion X absoluta)
                        const nL = lineaFisica.charCodeAt(i) || 0;
                        const nH = lineaFisica.charCodeAt(i + 1) || 0;
                        xDots = nL + (nH * 256);
                        pendienteX = true;
                        i += 2;
                        break;
                    }
                    case "\\": { // ESC \ n1 n2  (extension de ESC $)
                        const nL = lineaFisica.charCodeAt(i) || 0;
                        const nH = lineaFisica.charCodeAt(i + 1) || 0;
                        xDots += nL + (nH * 256);
                        pendienteX = true;
                        i += 2;
                        break;
                    }
                    case "4": { // ESC 4 nL nH
                        i += 2;
                        break;
                    }
                    case "@": // ESC @  (reset)
                        state = { align: "left", bold: false, large: false };
                        xDots = 0;
                        pendienteX = false;
                        lineTieneX = false;
                        lineAlign = state.align;
                        lineBold = state.bold;
                        lineLarge = state.large;
                        break;
                    default:
                        break;
                }
                continue;
            }

            if (c === "\x1D") { // GS
                const comando = lineaFisica[i + 1];
                i += 2;
                if (comando === "!") i += 1;      // GS ! n
                else if (comando === "v") i += 2; // GS v 0 m
                else if (comando === "V") i += 1; // GS V m
                continue;
            }

            aplicarX();
            lineAlign = state.align;
            lineBold = state.bold;
            lineLarge = state.large;
            line += c;
            i += 1;
        }

        pushLine();
    });

    return lines;
};

export const DOT_POR_MM = DOTS_POR_MM;
export const LOGO_MAX_DOTS = MAX_LOGO_WIDTH_DOTS;
